import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import {
  UsageManagementService,
  UsageStats,
} from "@/lib/services/usage-management";
import { toast } from "sonner";

export interface UsageLimitResult {
  allowed: boolean;
  remaining: number;
  limit: number;
  usagePercentage: number;
}

export function useUsageLimits() {
  const { user } = useAuth();
  const [usageStats, setUsageStats] = useState<UsageStats[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load usage stats
  const loadUsageStats = useCallback(async () => {
    if (!user) return;

    setLoading(true);
    setError(null);
    try {
      const stats = await UsageManagementService.getUserUsageStats(user.id);
      setUsageStats(stats);
    } catch (err) {
      console.error("Error loading usage stats:", err);
      setError("Error al cargar estadísticas de uso");
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Check if user can use a specific feature
  const checkFeatureUsage = useCallback(
    async (
      featureId: string,
      dailyLimit: number = 10
    ): Promise<UsageLimitResult> => {
      if (!user) {
        return {
          allowed: false,
          remaining: 0,
          limit: dailyLimit,
          usagePercentage: 0,
        };
      }

      try {
        const result = await UsageManagementService.checkAndIncrementUsage(
          user.id,
          featureId,
          dailyLimit
        );

        // Update local stats
        await loadUsageStats();

        return {
          allowed: result.allowed,
          remaining: result.remaining,
          limit: result.limit,
          usagePercentage: result.allowed
            ? ((result.limit - result.remaining) / result.limit) * 100
            : 100,
        };
      } catch (err) {
        console.error("Error checking feature usage:", err);
        return {
          allowed: false,
          remaining: 0,
          limit: dailyLimit,
          usagePercentage: 100,
        };
      }
    },
    [user, loadUsageStats]
  );

  // Get usage stats for a specific feature
  const getFeatureUsage = useCallback(
    (featureId: string): UsageStats | null => {
      return usageStats.find((stat) => stat.feature_id === featureId) || null;
    },
    [usageStats]
  );

  // Check if user has reached daily limit for a feature
  const hasReachedLimit = useCallback(
    (featureId: string): boolean => {
      const featureStats = getFeatureUsage(featureId);
      return featureStats
        ? featureStats.current_usage >= featureStats.daily_limit
        : false;
    },
    [getFeatureUsage]
  );

  // Get remaining usage for a feature
  const getRemainingUsage = useCallback(
    (featureId: string): number => {
      const featureStats = getFeatureUsage(featureId);
      return featureStats ? featureStats.remaining_usage : 0;
    },
    [getFeatureUsage]
  );

  // Show usage limit warning
  const showUsageWarning = useCallback(
    (featureId: string) => {
      const remaining = getRemainingUsage(featureId);
      const featureStats = getFeatureUsage(featureId);

      if (remaining <= 2 && remaining > 0) {
        toast.warning(
          `Te quedan ${remaining} requests disponibles para hoy en esta función.`,
          {
            description: "El límite diario se reinicia a medianoche.",
            duration: 5000,
          }
        );
      } else if (remaining === 0) {
        toast.error(
          "Has alcanzado el límite diario de requests para esta función.",
          {
            description: "El límite se reiniciará mañana a las 00:00.",
            duration: 7000,
          }
        );
      }
    },
    [getRemainingUsage, getFeatureUsage]
  );

  // Load stats on mount and when user changes
  useEffect(() => {
    loadUsageStats();
  }, [loadUsageStats]);

  return {
    usageStats,
    loading,
    error,
    checkFeatureUsage,
    getFeatureUsage,
    hasReachedLimit,
    getRemainingUsage,
    showUsageWarning,
    refreshStats: loadUsageStats,
  };
}
